/**
 * Convierte un objeto File a una cadena Base64
 * Útil para enviar archivos como parte de un JSON al backend
 * 
 * @param file - El archivo a convertir
 * @returns Promise que resuelve con la cadena Base64 (incluye data URL prefix)
 * @throws Error si hay problema al leer el archivo
 * 
 * @example
 * ```typescript
 * const file = inputElement.files[0];
 * const base64 = await fileToBase64(file);
 * // base64 = "data:image/png;base64,iVBORw0KGgo..."
 * ```
 */
/**
 * Convierte un objeto File a una cadena Base64
 * @param file - El archivo a convertir
 * @param withMetaData - Si es false, elimina el prefijo "data:<contentType>;base64," de la cadena
 * @returns Promise que resuelve con la cadena Base64
 */
export const fileToBase64 = (file: File, withMetaData: boolean = true): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const result = reader.result as string;
            if (withMetaData) {
                resolve(result);
            } else {
                /* Elimina el prefijo "data:<contentType>;base64," si existe */
                const base64 = result.replace(/^data:[^;]+;base64,/, "");
                resolve(base64);
            }
        };
        reader.onerror = error => reject(error);
    });
};

/**
 * Convierte múltiples archivos a Base64 en paralelo
 * 
 * @param files - Array de archivos a convertir
 * @returns Promise que resuelve con un array de cadenas Base64
 * 
 * @example
 * ```typescript
 * const files = [file1, file2, file3];
 * const base64Array = await filesToBase64(files);
 * ```
 */
export const filesToBase64 = async (files: File[], withMetaData: boolean = true): Promise<string[]> => {
    return Promise.all(files.map(file => fileToBase64(file, withMetaData)));
};

/**
 * Obtiene información de metadatos de un archivo sin convertirlo
 * 
 * @param file - El archivo del cual extraer metadatos
 * @returns Objeto con información del archivo
 * 
 * @example
 * ```typescript
 * const metadata = getFileMetadata(file);
 * // { name: "documento.pdf", size: 1024000, type: "application/pdf", sizeInKB: 1000 }
 * ```
 */
export const getFileMetadata = (file: File) => {
    return {
        name: file.name,
        size: file.size,
        type: file.type,
        sizeInKB: Math.round(file.size / 1024),
        sizeInMB: Math.round((file.size / 1024 / 1024) * 100) / 100,
    };
};
